/**
 * ResizeObserver Error Suppression Patch
 * 
 * This file patches ResizeObserver to prevent loop errors and suppresses
 * them from appearing in the React error overlay. It must be imported
 * BEFORE React loads.
 */

if (typeof window !== 'undefined') {
  // Helper to detect ResizeObserver errors
  const isResizeObserverError = (error: any): boolean => {
    if (!error) return false;
    const errorStr = String(error);
    const errorMessage = error?.message || error?.error?.message || '';
    const errorStack = error?.stack || '';
    return (
      errorStr.includes('ResizeObserver loop') ||
      errorMessage.includes('ResizeObserver loop') ||
      errorStack.includes('ResizeObserver loop') ||
      errorStr.includes('ResizeObserver loop limit exceeded') ||
      errorStr.includes('ResizeObserver loop completed with undelivered notifications')
    );
  };

  // Store original functions
  const OriginalResizeObserver = window.ResizeObserver;
  const originalConsoleError = window.console.error;
  const originalConsoleWarn = window.console.warn;

  // Patch ResizeObserver BEFORE anything else uses it
  // This prevents the loop from happening in the first place
  window.ResizeObserver = class PatchedResizeObserver extends OriginalResizeObserver {
    private callbacks: Map<ResizeObserverCallback, ResizeObserverCallback> = new Map();
    private rafId: number | null = null;
    private pendingEntries: ResizeObserverEntry[] = [];
    private pendingObserver: ResizeObserver | null = null;

    constructor(callback: ResizeObserverCallback) {
      // Wrap callback to catch and suppress errors
      const wrappedCallback: ResizeObserverCallback = (entries: ResizeObserverEntry[], observer: ResizeObserver) => {
        try {
          callback(entries, observer);
        } catch (error: any) {
          // Silently suppress ResizeObserver loop errors
          if (!isResizeObserverError(error)) {
            originalConsoleError(error);
          }
        }
      };

      // Use microtask queue to debounce callbacks and prevent loops
      const debouncedCallback: ResizeObserverCallback = (entries: ResizeObserverEntry[], observer: ResizeObserver) => {
        this.pendingEntries = entries;
        this.pendingObserver = observer;

        if (this.rafId !== null) {
          cancelAnimationFrame(this.rafId);
        }

        this.rafId = requestAnimationFrame(() => {
          // Use queueMicrotask to ensure this runs after layout calculations
          queueMicrotask(() => {
            try {
              wrappedCallback(this.pendingEntries, this.pendingObserver!);
            } catch (error: any) {
              // Suppress ResizeObserver errors
              if (!isResizeObserverError(error)) {
                originalConsoleError(error);
              }
            }
            this.rafId = null;
          });
        });
      };

      super(debouncedCallback);
      this.callbacks.set(callback, debouncedCallback);
    }

    observe(target: Element, options?: ResizeObserverOptions): void {
      try {
        super.observe(target, options);
      } catch (error: any) {
        if (!isResizeObserverError(error)) {
          throw error;
        }
      }
    }

    unobserve(target: Element): void {
      try {
        super.unobserve(target);
      } catch (error: any) {
        if (!isResizeObserverError(error)) {
          throw error;
        }
      }
    }

    disconnect(): void {
      if (this.rafId !== null) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      try {
        super.disconnect();
      } catch (error: any) {
        if (!isResizeObserverError(error)) {
          throw error;
        }
      }
    }
  };

  // Suppress console errors for ResizeObserver
  window.console.error = (...args: any[]) => {
    if (isResizeObserverError(args[0]) || isResizeObserverError(args[1])) {
      return;
    }
    originalConsoleError.apply(console, args);
  };

  window.console.warn = (...args: any[]) => {
    if (isResizeObserverError(args[0]) || isResizeObserverError(args[1])) {
      return;
    }
    originalConsoleWarn.apply(console, args);
  };

  // Suppress global error events
  const handleError = (event: ErrorEvent) => {
    if (isResizeObserverError(event.error) || isResizeObserverError(event.message)) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      return false;
    }
  };
  window.addEventListener('error', handleError, true);

  // Suppress unhandled promise rejections
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    if (isResizeObserverError(event.reason)) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
  }, true);

  // Aggressively intercept React error overlay
  const interceptErrorOverlay = () => {
    // Method 1: Patch the global hook
    if ((window as any).__REACT_ERROR_OVERLAY_GLOBAL_HOOK__) {
      const hook = (window as any).__REACT_ERROR_OVERLAY_GLOBAL_HOOK__;
      
      // Patch onError
      if (hook.onError) {
        const originalOnError = hook.onError;
        hook.onError = function(...args: any[]) {
          if (isResizeObserverError(args[0])) {
            return;
          }
          return originalOnError.apply(this, args);
        };
      }

      // Patch handleError - this is the key function
      if (hook.handleError) {
        const originalHandleError = hook.handleError;
        hook.handleError = function(...args: any[]) {
          const error = args[0];
          if (isResizeObserverError(error)) {
            return;
          }
          return originalHandleError.apply(this, args);
        };
      }

      // Patch showErrorOverlay if it exists
      if (hook.showErrorOverlay) {
        const originalShowErrorOverlay = hook.showErrorOverlay;
        hook.showErrorOverlay = function(...args: any[]) {
          const error = args[0];
          if (isResizeObserverError(error)) {
            return;
          }
          return originalShowErrorOverlay.apply(this, args);
        };
      }

      // Filter runtimeErrors array
      Object.defineProperty(hook, 'runtimeErrors', {
        get: function() {
          return (this._runtimeErrors || []).filter((err: any) => !isResizeObserverError(err));
        },
        set: function(errors: any[]) {
          this._runtimeErrors = (errors || []).filter((err: any) => !isResizeObserverError(err));
        },
        configurable: true,
        enumerable: true,
      });
    }

    // Method 2: Patch react-error-overlay directly
    if ((window as any).__REACT_ERROR_OVERLAY__) {
      const overlay = (window as any).__REACT_ERROR_OVERLAY__;
      if (overlay.handleRuntimeError) {
        const originalHandleRuntimeError = overlay.handleRuntimeError;
        overlay.handleRuntimeError = function(...args: any[]) {
          if (isResizeObserverError(args[0])) {
            return;
          }
          return originalHandleRuntimeError.apply(this, args);
        };
      }
    }

    // Method 3: Use MutationObserver to catch error overlay when it's added to DOM
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) {
            const element = node as Element;
            // Check if this is the error overlay
            if (element.id === 'react-error-overlay' || 
                element.classList.contains('react-error-overlay') ||
                element.querySelector('#react-error-overlay')) {
              // Try to intercept again
              interceptErrorOverlay();
            }
          }
        });
      });
    });

    observer.observe(document.body || document.documentElement, {
      childList: true,
      subtree: true,
    });
  };

  // Try to intercept immediately
  interceptErrorOverlay();

  // Also intercept on DOMContentLoaded and after delays
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', interceptErrorOverlay);
  } else {
    interceptErrorOverlay();
  }

  // Multiple retry attempts
  setTimeout(interceptErrorOverlay, 0);
  setTimeout(interceptErrorOverlay, 100);
  setTimeout(interceptErrorOverlay, 500);
  setTimeout(interceptErrorOverlay, 1000);
  setTimeout(interceptErrorOverlay, 2000);
}

export {};